import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminApi } from '../lib/api';
import { copy } from '../lib/copy';
import { formatDate } from '../lib/utils';
import { formatYesNo } from '../lib/copy';
import { EmptyState, ErrorState, LoadingState, PageHeader } from '../components/ui';
import { useProductScope } from '../context/ProductScopeContext';

export function TelegramBusinessPage() {
  const scope = useProductScope();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const botsQuery = useQuery({
    queryKey: ['telegram', 'bots'],
    queryFn: () => adminApi.telegramBots(),
  });

  const diagnosticsQuery = useQuery({
    queryKey: ['telegram', 'bots', selectedId, 'diagnostics'],
    queryFn: () => adminApi.telegramBotDiagnostics(selectedId!),
    enabled: Boolean(selectedId),
  });

  return (
    <div>
      <PageHeader title={copy.telegram.title} description={copy.telegram.description} />
      {botsQuery.isLoading ? <LoadingState /> : null}
      {botsQuery.isError ? (
        <ErrorState message={copy.errors.loadTelegram} onRetry={() => botsQuery.refetch()} />
      ) : null}
      {botsQuery.data?.items.length === 0 ? <EmptyState message={copy.telegram.empty} /> : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="ops-card overflow-hidden p-0">
          {botsQuery.data && botsQuery.data.items.length > 0 ? (
            <table className="ops-table">
              <thead>
                <tr>
                  <th>{copy.telegram.bot}</th>
                  <th>{copy.telegram.status}</th>
                  <th>{copy.telegram.webhook}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {botsQuery.data.items.map((bot) => (
                  <tr key={bot.id}>
                    <td>
                      <p className="font-medium">@{bot.bot_username}</p>
                      <p className="text-xs text-ops-muted">{bot.agent_name}</p>
                    </td>
                    <td>{bot.status}</td>
                    <td>{formatYesNo(bot.webhook_ok)}</td>
                    <td>
                      <button
                        type="button"
                        className="ops-btn-secondary py-1 text-xs"
                        onClick={() => setSelectedId(bot.id)}
                      >
                        {copy.telegram.diagnostics}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>

        <div className="ops-card">
          {!selectedId ? (
            <p className="text-sm text-ops-muted">{copy.telegram.diagnostics}</p>
          ) : diagnosticsQuery.isLoading ? (
            <LoadingState />
          ) : diagnosticsQuery.isError ? (
            <ErrorState message={copy.errors.loadTelegram} onRetry={() => diagnosticsQuery.refetch()} />
          ) : diagnosticsQuery.data ? (
            <div className="space-y-3 text-sm">
              <p>
                <span className="text-ops-muted">{copy.telegram.capability}: </span>
                {diagnosticsQuery.data.capability_status}
              </p>
              <p className="text-ops-secondary">{diagnosticsQuery.data.capability_message}</p>
              {diagnosticsQuery.data.bot ? (
                <p>
                  {copy.telegram.bot}: {diagnosticsQuery.data.bot.username ?? diagnosticsQuery.data.bot.firstName}
                </p>
              ) : null}
              {diagnosticsQuery.data.webhook ? (
                <>
                  <p>
                    {copy.telegram.webhookUrl}: {diagnosticsQuery.data.webhook.url ?? copy.common.notAvailable}
                  </p>
                  <p>
                    {copy.telegram.pendingUpdates}: {diagnosticsQuery.data.webhook.pendingUpdateCount}
                  </p>
                  <p>
                    {copy.telegram.lastError}:{' '}
                    {diagnosticsQuery.data.webhook.lastErrorMessage ?? copy.common.notAvailable}
                  </p>
                </>
              ) : null}
              <p className="text-ops-muted">
                {copy.telegram.lastCheck}: {formatDate(diagnosticsQuery.data.checked_at)}
              </p>
              {botsQuery.data?.items.find((b) => b.id === selectedId) ? (
                <Link
                  to={`/${scope}/users/${botsQuery.data.items.find((b) => b.id === selectedId)!.user_id}`}
                  className="text-ops-text hover:underline"
                >
                  {copy.common.openProfile}
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
